import i18n from 'i18n';
import { WellframeApi } from 'store/api';

export type BiometricType = 'weight' | 'blood_pressure' | 'blood_glucose';

export interface BiometricDataset {
  name: string;
  values: string[];
  labels: string[];
}

export interface BiometricDatapoint {
  size: number;
  format: string;
  labels: string[];
}

export interface BiometricDataPoint {
  id: number;
  // Single-value types (e.g. weight) use `value`; multi-value types (blood pressure) use `values`.
  value?: number;
  values?: number[];
  date: string;
  timestamp: string;
  dataset?: string;
  source: string;
  device?: string;
}

export interface BiometricsResponse {
  userId: number;
  dataType: string;
  dataTypeLabel: string;
  units: string;
  precision: number;
  dataset?: BiometricDataset;
  datapoint: BiometricDatapoint;
  data: BiometricDataPoint[];
}

export interface GetBiometricsArgs {
  biometricType: BiometricType;
  userId: number;
  /** yyyy-MM-dd */
  startDate?: string;
  /** yyyy-MM-dd */
  endDate?: string;
}

export interface BiometricDatapointRecord {
  id: number;
  programUserId: number;
  biometricType: {
    id: number;
    type: string;
    unit: string;
    displayName: string;
  };
  value: number;
  timeOfReading: string;
  userLocalDateCaptured: string;
  dataSourceType: string;
  dataSourceDeviceModel: string | null;
  relationshipToMeal: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GetBiometricDatapointsArgs {
  programUserId: number;
  biometricType: BiometricType;
  /** yyyy-MM-dd */
  startDate?: string;
  /** yyyy-MM-dd */
  endDate?: string;
}

export interface DownloadBiometricHistoryArgs {
  biometricType: BiometricType;
  userId: number;
}

export const biometricsService = WellframeApi.injectEndpoints({
  endpoints: (builder) => ({
    getBiometrics: builder.query<BiometricsResponse, GetBiometricsArgs>({
      query: ({ biometricType, userId, startDate, endDate }) => ({
        url: `/mobileapi/v2/biometrics/${biometricType}/users/${userId}`,
        params: {
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
        },
        headers: {
          'Accept-Language': i18n.language,
        },
      }),
    }),
    // Hits the separate biometrics service (routed by the /api/biometrics/ prefix).
    getBiometricDatapoints: builder.query<BiometricDatapointRecord[], GetBiometricDatapointsArgs>({
      query: ({ programUserId, biometricType, startDate, endDate }) => ({
        url: `/api/biometrics/v1/program_users/${programUserId}/biometric_datapoints`,
        params: {
          biometricType,
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
        },
        headers: {
          'Accept-Language': i18n.language,
        },
      }),
    }),

    downloadBiometricData: builder.mutation<Blob, DownloadBiometricHistoryArgs>({
      query: ({ biometricType, userId }) => ({
        url: `/mobileapi/v1/biometrics/${biometricType}/users/${userId}/download`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),
  }),
});

export const {
  useGetBiometricsQuery,
  useGetBiometricDatapointsQuery,
  useDownloadBiometricDataMutation,
} = biometricsService;
